import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Does insurance cover SAINT® TMS?",
    answer:
      "Sometimes, and it depends on your plan. SAINT® is billed under Category III CPT codes (0889T–0892T). The manufacturer reports Medicare fee-for-service reimbursement and growing commercial coverage, but many plans still treat SAINT® as investigational, prior authorization is common, and many patients self-pay. The only way to know your situation is benefits verification and, where required, prior authorization. Neuro Wellness verifies benefits at no cost.",
  },
  {
    question: "Why is SAINT® coverage different from traditional TMS coverage?",
    answer:
      "Traditional TMS is billed under established CPT codes (90867–90869) and has been covered by many commercial plans, Medicare, and the VA for years when medical-necessity criteria are met. SAINT® uses newer Category III CPT codes (0889T–0892T), which are tracking codes for emerging technologies. Because these codes are newer, many plans have not yet established coverage policies for them, and some treat SAINT® as investigational.",
  },
  {
    question: "What is prior authorization, and does SAINT® require it?",
    answer:
      "Prior authorization is a clinical review in which your insurance plan decides, based on your medical records, that treatment appears medically necessary before approving it. Many plans require prior authorization for SAINT®. This means your clinic submits documentation — diagnosis, medication history, and clinical justification — and the plan reviews it before treatment. Prior authorization is not a guarantee of payment.",
  },
  {
    question:
      "What is the difference between benefits verification and prior authorization?",
    answer:
      "Benefits verification is a check of whether your plan is active and whether the relevant codes are covered, what your cost-sharing is, and whether authorization is needed. Prior authorization is a separate clinical review in which the plan decides whether treatment is medically necessary based on your records. Neither guarantees payment; a claim can still be denied for coding, setting, or eligibility reasons.",
  },
  {
    question: "Does Blue Cross Blue Shield cover SAINT® TMS?",
    answer:
      "Blue Cross Blue Shield is a federation of independent plans, so coverage is plan-specific. Most BCBS plans have a written medical policy for traditional TMS for major depressive disorder, but their positions on SAINT®'s Category III codes vary. Contact the number on your card, ask for the TMS medical policy, and have your clinic verify benefits. Neuro Wellness can assist with BCBS benefit verification.",
  },
  {
    question: "What if my insurance denies SAINT® but covers traditional TMS?",
    answer:
      "This is a common situation. The clinical eligibility criteria for the two treatments overlap heavily, so most people who qualify for SAINT® also qualify for traditional TMS, which is covered under established codes when medical-necessity criteria are met. Discuss the trade-offs with your psychiatrist: traditional TMS takes four to six weeks of daily visits, while SAINT® is five days but may be self-pay. You can also appeal the SAINT® denial.",
  },
  {
    question: "Can Neuro Wellness help me verify my benefits?",
    answer:
      "Yes. Neuro Wellness TMS Centers of America verifies your benefits at no cost and provides a written estimate before treatment begins. The clinic contacts your insurance carrier to confirm current coverage, deductible status, and authorization requirements. However, benefits verification is a courtesy service and does not guarantee coverage — final determination is made by your insurance carrier.",
  },
];

export const doesInsuranceCoverSaint: LearnPageData = {
  slug: "does-insurance-cover-saint-tms",
  title: "Does Insurance Cover SAINT® TMS? | Neuro Wellness",
  description:
    "Insurance coverage for SAINT® TMS explained: Category III CPT codes, prior authorization, medical necessity, benefits verification, and what to ask your plan.",
  canonical: "/learn/is-saint-tms-covered-by-insurance",
  h1: "Does Insurance Cover SAINT® TMS?",
  breadcrumbLabel: "Does Insurance Cover SAINT® TMS?",
  crumbs: baseCrumbs("Does Insurance Cover SAINT® TMS?"),
  shortAnswer:
    "Insurance coverage for SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) varies by plan and is currently more limited than for traditional TMS. SAINT® is billed under Category III CPT codes (0889T–0892T), which are newer tracking codes. The manufacturer reports Medicare fee-for-service reimbursement and growing commercial coverage, but many plans still treat SAINT® as investigational, prior authorization is common, and many patients self-pay. Neuro Wellness TMS Centers of America verifies your benefits at no cost and provides a written estimate. Benefits verification is not a guarantee of payment.",
  keyTakeaways: [
    "Coverage for SAINT® varies by plan and is currently more limited than for traditional TMS.",
    "SAINT® uses Category III CPT codes (0889T–0892T), which differ from traditional TMS codes.",
    "Prior authorization is commonly required — your clinic submits clinical documentation for the plan to review.",
    "Benefits verification confirms whether your plan covers the codes, but does not guarantee payment.",
    "Neuro Wellness verifies your benefits at no cost and provides a written estimate before treatment.",
  ],

  sections: [
    {
      heading: "How SAINT® Insurance Coverage Works",
      body: (
        <>
          {P(
            "Insurance coverage for SAINT® depends on your specific plan, and it is handled differently from traditional TMS. Traditional TMS is billed under established CPT codes (90867, 90868, 90869) and has been covered by many commercial plans, Medicare, and the VA for years when medical-necessity criteria are met.",
          )}
          {P(
            "SAINT® is billed under Category III CPT codes (0889T–0892T). Category III codes are tracking codes for emerging technologies — they allow insurers to collect data on new procedures before establishing permanent coverage policies. Because these codes are newer, many plans have not yet established coverage policies for them, and some treat SAINT® as investigational.",
          )}
          {P(
            "The manufacturer, Magnus Medical, reports Medicare fee-for-service reimbursement and growing commercial coverage. However, coverage is not universal, prior authorization is common, and many patients currently self-pay. The only way to know your situation is to verify your specific plan.",
          )}
        </>
      ),
    },
    {
      heading:
        "Traditional TMS vs. Accelerated Protocols: How Insurers Handle Them Differently",
      body: (
        <>
          {P(
            "Insurers may handle traditional TMS and accelerated protocols like SAINT® differently, even when both are FDA-cleared. Traditional TMS has a long track record of coverage under established codes. SAINT®'s Category III codes are newer, and each plan sets its own policy on whether to cover them.",
          )}
          {P(
            "Some plans cover SAINT® after prior authorization; others deny it as investigational or experimental. A plan that covers traditional TMS may not cover SAINT®, and vice versa. This is why benefits verification is essential before starting treatment — assumptions based on traditional TMS coverage do not necessarily apply to SAINT®.",
          )}
        </>
      ),
    },
    {
      heading: "Prior Authorization and Medical Necessity",
      body: (
        <>
          {P(
            "Prior authorization is a clinical review in which your insurance plan decides, based on your medical records, that treatment appears medically necessary before approving it. Many plans require prior authorization for SAINT®.",
          )}
          {P(
            "Medical-necessity criteria typically include a diagnosis of major depressive disorder, documentation of prior antidepressant trials (including dose and duration), psychiatrist oversight, and the absence of exclusions. Your clinic submits this documentation, and the plan reviews it before treatment. If denied, you can request the reason, ask your psychiatrist about a peer-to-peer review, and file an appeal within the deadline.",
          )}
          {P(
            "Prior authorization is not a guarantee of payment. Even after authorization, a claim can be denied for coding, setting, or eligibility reasons.",
          )}
        </>
      ),
    },
    {
      heading: "Benefits Verification vs. Prior Authorization",
      body: (
        <>
          {P(
            "These two terms are often confused, but they are different steps:",
          )}
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Benefits verification</strong> — The clinic confirms your
              plan is active and asks whether the codes are covered, what your
              cost-sharing is, and whether authorization is needed.
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Prior authorization</strong> — A clinical review in which
              the plan decides, based on your records, that treatment appears
              medically necessary.
            </li>
          </ul>
          {P(
            "Neither guarantees payment. A claim can still be denied after treatment for coding, setting, or eligibility reasons. Benefits verification is a courtesy service that helps you understand your likely costs before starting treatment.",
          )}
        </>
      ),
    },
    {
      heading: "What Information Neuro Wellness Needs to Verify Your Benefits",
      body: (
        <>
          {P("To verify your benefits, Neuro Wellness typically needs:")}
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Your insurance card (front and back) or member ID and group number
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Your date of birth and the policyholder's information if it is not
              you
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Basic clinical information (diagnosis and medication history) for
              prior authorization if required
            </li>
          </ul>
          {P(
            "The clinic contacts your insurance carrier, confirms current coverage, checks deductible status, and identifies authorization requirements. You receive a written estimate before treatment begins.",
          )}
        </>
      ),
    },
    {
      heading: "What to Do If SAINT® Is Denied but Traditional TMS Is Covered",
      body: (
        <>
          {P(
            "This is a common situation. The clinical eligibility criteria for SAINT® and traditional TMS overlap heavily, so most people who qualify for SAINT® also qualify for traditional TMS, which is covered under established codes when medical-necessity criteria are met.",
          )}
          {P(
            "If your plan denies SAINT® but covers traditional TMS, you have options. You can discuss the trade-offs with your psychiatrist: traditional TMS takes four to six weeks of daily visits, while SAINT® is five days but may require self-pay. You can also appeal the SAINT® denial — ask for the reason, request a peer-to-peer review, and file an appeal within the deadline.",
          )}
          {P(
            <>
              Neuro Wellness offers both SAINT® and traditional TMS.{" "}
              <a
                href="/am-i-a-candidate"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Request a consultation
              </a>{" "}
              to discuss which option fits your situation and coverage.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Neuro Wellness TMS Centers of America",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America verifies your benefits at no cost and provides a written estimate before treatment begins. The clinic offers both SAINT® on the Magnus Neuromodulation System and traditional TMS, so if SAINT® is not covered by your plan, the clinical team can discuss traditional TMS as an alternative.",
          )}
          {P(
            "Coverage and benefits vary by plan and individual circumstances. Neuro Wellness can help patients verify their benefits but cannot guarantee coverage. To start the verification process, call 954-827-9793 or request a consultation online.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "American Medical Association. CPT Category III codes 0889T–0892T (SAINT® / accelerated TMS).",
      url: "https://www.ama-assn.org/practice-management/cpt",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® reimbursement and coverage information.",
      url: "https://magnusmedical.com",
    },
    {
      citation:
        "Centers for Medicare & Medicaid Services. Medicare Coverage Database — TMS Local Coverage Determinations.",
      url: "https://www.cms.gov/medicare-coverage-database",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/does-insurance-cover-saint-tms#webpage`,
        name: "Does Insurance Cover SAINT® TMS?",
        url: `${SITE_URL}/learn/does-insurance-cover-saint-tms`,
        description:
          "Insurance coverage for SAINT® TMS explained: Category III CPT codes, prior authorization, medical necessity, benefits verification, and what to ask your plan.",
        about: [
          { "@id": `${SITE_URL}/learn/does-insurance-cover-saint-tms#therapy` },
          { "@id": `${SITE_URL}/#condition-mdd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/does-insurance-cover-saint-tms#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177); billed under Category III CPT codes 0889T–0892T.",
        relevantSpecialty: "Psychiatric",
        code: [
          { "@type": "MedicalCode", code: "0889T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0890T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0891T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0892T", codingSystem: "CPT" },
        ],
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(faqs, `${SITE_URL}/learn/does-insurance-cover-saint-tms#faq`),
      breadcrumbJsonld(baseCrumbs("Does Insurance Cover SAINT® TMS?")),
    ],
  },
  relatedLinks: [
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "Cost components, self-pay, and financing options",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol at our clinic",
    },
    {
      title: "SAINT® TMS Insurance Coverage (detailed)",
      href: "/saint-tms-insurance-coverage",
      description: "CPT codes, verification, and appeals process",
    },
    {
      title: "Cost & Insurance Overview",
      href: "/cost-insurance",
      description: "Benefits verification and self-pay options",
    },
    {
      title: "BCBS Coverage for SAINT®",
      href: "/insurance/blue-cross-blue-shield-saint-tms",
      description: "How BCBS plans handle SAINT® coverage",
    },
    {
      title: "Check My Benefits",
      href: "/am-i-a-candidate",
      description: "Request a free benefits verification",
    },
  ],
};
